import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Gift, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function GiftCardResult({ variant }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [card, setCard] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (variant === "cancel" || !sessionId) { setStatus(variant === "cancel" ? "cancelled" : "error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/giftcards/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setCard(data.giftcard); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch {}
      attempts += 1;
      if (attempts > 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, variant]);

  const copy = () => {
    if (!card?.code) return;
    navigator.clipboard.writeText(card.code);
    setCopied(true); toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-28 pb-16 px-6 surface-alt">
      <Seo title="Gift Card | Keys Event Center" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card p-10 max-w-lg w-full text-center" data-testid="giftcard-result">
        {status === "checking" && (<>
          <Loader2 className="mx-auto text-[#ff5147] animate-spin mb-5" size={56} />
          <h1 className="font-display text-2xl font-bold text-[#f6ecd4]">Confirming your payment…</h1>
          <p className="text-[#a9bdbd] mt-2">Hang tight, this only takes a moment.</p>
        </>)}
        {status === "paid" && card && (<>
          <Gift className="mx-auto text-[#2fd0c6] mb-5" size={64} />
          <h1 className="font-display text-3xl font-extrabold text-[#f6ecd4]">Gift Card Purchased!</h1>
          <p className="text-[#a9bdbd] mt-3">
            {card.sent_to_recipient
              ? `We've emailed the ${card.amount ? `$${card.amount}` : ""} gift card to ${card.recipient_name || "your recipient"}. A copy is on its way to you too.`
              : "Your gift card code is below and has been emailed to you."}
          </p>
          <div className="rounded-2xl border-2 border-dashed border-[rgba(47,208,198,0.5)] p-6 mt-6" data-testid="giftcard-code-box">
            <div className="font-mono-arcade text-4xl font-extrabold text-[#ff5147]">${card.amount}</div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="font-mono-arcade text-lg tracking-widest text-[#f6ecd4]" data-testid="giftcard-code">{card.code}</span>
              <button onClick={copy} className="text-[#2fd0c6] hover:text-[#f6ecd4]" data-testid="giftcard-copy">{copied ? <Check size={18} /> : <Copy size={18} />}</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/reservations" className="btn-primary inline-flex items-center gap-2 px-8 py-3.5">Book a Party</Link>
            <Link to="/" className="btn-outline inline-flex items-center gap-2 px-8 py-3.5">Back to Home</Link>
          </div>
        </>)}
        {["cancelled", "failed", "error", "timeout"].includes(status) && (<>
          <XCircle className="mx-auto text-[#ff5147] mb-5" size={64} />
          <h1 className="font-display text-2xl font-bold text-[#f6ecd4]">{status === "cancelled" ? "Purchase Cancelled" : status === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="text-[#a9bdbd] mt-2">{status === "timeout" ? "Your payment is taking longer than expected. Check your email or contact us if you were charged." : "No worries, you weren't charged. You can try again anytime."}</p>
          <Link to="/gift-cards" className="btn-outline inline-flex px-8 py-3.5 mt-7">Try Again</Link>
        </>)}
      </motion.div>
    </div>
  );
}
